import http from "@/services/http";
import type {
  Withdrawal,
  PaginatedOperations,
  OperationListParams,
  ConfirmOperationRequest,
  AdminProcessRequest,
  OperationNote,
  CreateNoteRequest,
} from "@/models/operation.model";
import { normalizeWithdrawalNote } from "@/models/operation.model";

class WithdrawalService {
  private buildNotePayload(data: CreateNoteRequest): FormData | { content: string } {
    const content = data.content?.trim();
    if (data.image) {
      const formData = new FormData();
      if (content) {
        formData.append("content", content);
      }
      formData.append("image", data.image);
      return formData;
    }

    return { content: content || "" };
  }

  async getAll(
    params: OperationListParams = {}
  ): Promise<PaginatedOperations<Withdrawal>> {
    return http.get<PaginatedOperations<Withdrawal>>("/withdrawals", params);
  }

  async getAvailable(params: OperationListParams = {}): Promise<PaginatedOperations<Withdrawal>> {
    return http.get<PaginatedOperations<Withdrawal>>("/provider/withdrawals", {
      ...params,
      view: "Available",
    });
  }

  async getMine(params: OperationListParams = {}): Promise<PaginatedOperations<Withdrawal>> {
    return http.get<PaginatedOperations<Withdrawal>>("/provider/withdrawals", {
      ...params,
      view: "Mine",
    });
  }

  async getById(id: string): Promise<Withdrawal> {
    return http.get<Withdrawal>(`/withdrawals/${id}`);
  }

  async take(withdrawalId: string): Promise<Withdrawal> {
    return http.post<Withdrawal>(`/provider/withdrawals/${withdrawalId}/take`);
  }

  async confirm(withdrawalId: string, data: ConfirmOperationRequest): Promise<void> {
    return http.post<void>(`/provider/withdrawals/${withdrawalId}/confirm`, data);
  }

  async release(withdrawalId: string): Promise<Withdrawal> {
    return http.post<Withdrawal>(`/provider/withdrawals/${withdrawalId}/release`);
  }

  async adminProcess(withdrawalId: string, data: AdminProcessRequest): Promise<Withdrawal> {
    return http.post<Withdrawal>(`/admin/withdrawals/${withdrawalId}/process`, data);
  }

  async adminRelease(withdrawalId: string): Promise<Withdrawal> {
    return http.post<Withdrawal>(`/admin/withdrawals/${withdrawalId}/release`);
  }

  async getNotes(withdrawalId: string): Promise<OperationNote[]> {
    const notes = await http.get<any[]>(`/withdrawals/${withdrawalId}/notes`);
    return notes.map(normalizeWithdrawalNote);
  }

  async createNote(withdrawalId: string, data: CreateNoteRequest): Promise<OperationNote> {
    const payload = this.buildNotePayload(data);
    const note =
      payload instanceof FormData
        ? await http.postFormData<any>(`/withdrawals/${withdrawalId}/notes`, payload)
        : await http.post<any>(`/withdrawals/${withdrawalId}/notes`, payload);
    return normalizeWithdrawalNote(note);
  }

  async markNoteViewed(withdrawalId: string, noteId: string): Promise<void> {
    return http.patch<void>(`/withdrawals/${withdrawalId}/notes?noteId=${noteId}`);
  }

  async markAllNotesViewed(withdrawalId: string): Promise<void> {
    return http.patch<void>(`/withdrawals/${withdrawalId}/notes`);
  }
}

export default new WithdrawalService();
